import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Star, ArrowLeft, Loader2, User, Calendar, MessageSquare,
  TrendingUp, ThumbsUp, ThumbsDown, Send
} from "lucide-react";

const StarDisplay = ({ rating, size = "sm" }) => {
  const sizeClass = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-white/10"}`}
        />
      ))}
    </div>
  );
};

const RatingsManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("all");
  const [minRating, setMinRating] = useState("all");
  const [driverStats, setDriverStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchRatings();
  }, [selectedDriver, minRating]);

  useEffect(() => {
    if (selectedDriver && selectedDriver !== "all") {
      fetchDriverStats(selectedDriver);
    } else {
      setDriverStats(null);
    }
  }, [selectedDriver]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, driversRes] = await Promise.all([
        api.get("/ratings/stats"),
        api.get("/users/drivers")
      ]);
      setStats(statsRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error("Failed to load ratings data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedDriver && selectedDriver !== "all") params.append("driver_id", selectedDriver);
      if (minRating && minRating !== "all") params.append("min_rating", minRating);
      
      const response = await api.get(`/ratings?${params.toString()}`);
      setRatings(response.data);
    } catch (error) {
      console.error("Failed to fetch ratings:", error);
    }
  };

  const fetchDriverStats = async (driverId) => {
    try {
      const response = await api.get(`/ratings/driver/${driverId}`);
      setDriverStats(response.data);
    } catch (error) {
      console.error("Failed to fetch driver stats:", error);
    }
  };

  const handleRequestRating = async (bookingId) => {
    try {
      await api.post(`/ratings/request/${bookingId}`);
      toast.success("Rating request sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send rating request");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8" data-testid="ratings-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold">Customer Ratings</h1>
            <p className="text-muted-foreground">Monitor customer feedback and driver performance</p>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="border border-black/[0.06]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.average_rating?.toFixed(1) || "0.0"}</p>
                    <p className="text-xs text-muted-foreground">Average Rating</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-black/[0.06]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_ratings || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Reviews</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-black/[0.06]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <ThumbsUp className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.breakdown?.[5] + stats.breakdown?.[4] || 0}</p>
                    <p className="text-xs text-muted-foreground">Positive (4-5★)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-black/[0.06]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <ThumbsDown className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.breakdown?.[1] + stats.breakdown?.[2] || 0}</p>
                    <p className="text-xs text-muted-foreground">Needs Attention (1-2★)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rating Distribution */}
        {stats && (
          <Card className="border border-black/[0.06] mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Rating Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = stats.breakdown?.[star] || 0;
                  const percentage = stats.total_ratings > 0 ? (count / stats.total_ratings) * 100 : 0;
                  
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-16">
                        <span className="text-sm font-medium">{star}</span>
                        <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                      </div>
                      <div className="flex-1 h-3 bg-white/[0.04] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            star >= 4 ? "bg-green-500" : star === 3 ? "bg-yellow-500" : "bg-red-500"
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Filters & Ratings List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="flex gap-3">
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger className="w-48" data-testid="driver-filter">
                  <SelectValue placeholder="All Drivers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drivers</SelectItem>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={minRating} onValueChange={setMinRating}>
                <SelectTrigger className="w-36" data-testid="rating-filter">
                  <SelectValue placeholder="All Ratings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4+ Stars</SelectItem>
                  <SelectItem value="3">3+ Stars</SelectItem>
                  <SelectItem value="1">1-2 Stars</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ratings List */}
            <Card className="border border-black/[0.06]">
              <CardContent className="p-0">
                {ratings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>No ratings found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-black/[0.04]">
                    {ratings.map((rating) => (
                      <div key={rating.id} className="p-4" data-testid={`rating-${rating.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <StarDisplay rating={rating.rating} />
                              <span className="font-mono text-sm text-primary">{rating.booking_ref}</span>
                            </div>
                            <p className="font-medium">{rating.passenger_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {new Date(rating.created_at).toLocaleDateString()}
                            </p>
                            {rating.driver_name && (
                              <p className="text-xs text-muted-foreground">Driver: {rating.driver_name}</p>
                            )}
                          </div>
                        </div>
                        
                        {rating.comment && (
                          <p className="text-sm text-muted-foreground bg-white/[0.02] p-3 rounded-lg mt-2">
                            &ldquo;{rating.comment}&rdquo;
                          </p>
                        )}

                        {(rating.driver_rating || rating.punctuality_rating) && (
                          <div className="flex gap-4 mt-2 text-xs">
                            {rating.driver_rating && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                Driver: {rating.driver_rating}★
                              </span>
                            )}
                            {rating.punctuality_rating && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Punctuality: {rating.punctuality_rating}★
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Driver Stats Sidebar */}
          <div className="space-y-4">
            {driverStats && (
              <Card className="border border-primary/20 bg-primary/[0.02]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Driver Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{drivers.find(d => d.id === selectedDriver)?.name}</p>
                      <div className="flex items-center gap-1">
                        <StarDisplay rating={Math.round(driverStats.average_rating)} size="sm" />
                        <span className="text-sm text-muted-foreground">({driverStats.average_rating?.toFixed(1)})</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-white/[0.02]">
                    <p className="text-xs text-muted-foreground mb-2">Rating Breakdown</p>
                    {[5, 4, 3, 2, 1].map((star) => (
                      <div key={star} className="flex items-center gap-2 text-sm">
                        <span className="w-8">{star}★</span>
                        <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-400 rounded-full"
                            style={{ width: `${(driverStats.rating_breakdown?.[star] / driverStats.total_ratings) * 100 || 0}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-muted-foreground">{driverStats.rating_breakdown?.[star] || 0}</span>
                      </div>
                    ))}
                  </div>

                  <div className="text-center p-3 rounded-lg bg-green-500/10">
                    <p className="text-2xl font-bold text-green-500">{driverStats.total_ratings}</p>
                    <p className="text-xs text-muted-foreground">Total Reviews</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Rated Drivers */}
            <Card className="border border-black/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  Top Rated Drivers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {drivers
                    .filter(d => d.average_rating)
                    .sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0))
                    .slice(0, 5)
                    .map((driver, index) => (
                      <div key={driver.id} className="flex items-center gap-3">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? "bg-yellow-500 text-black" :
                          index === 1 ? "bg-gray-400 text-black" :
                          index === 2 ? "bg-amber-700 text-white" :
                          "bg-white/[0.04] text-muted-foreground"
                        }`}>
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{driver.name}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-mono">{driver.average_rating?.toFixed(1) || "-"}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingsManagementPage;
